import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { normalizeWallet } from "@hupper/shared";
import { usersApi, avatarUrl, type UserProfile } from "../auth/api";

/**
 * Last-known avatarVersion per wallet, persisted so the very first paint
 * after a reload shows the uploaded photo (straight from the browser's
 * image cache) instead of flashing the generated initial while GET /me is
 * in flight. The fresh profile overwrites it as soon as it lands.
 */
const avatarVersionKey = (wallet: string) => `hupper:avatarVersion:${normalizeWallet(wallet)}`;

function readCachedAvatarVersion(wallet: string | null): number | undefined {
  if (!wallet) return undefined;
  try {
    const n = Number(localStorage.getItem(avatarVersionKey(wallet)));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch {
    return undefined;
  }
}

function writeCachedAvatarVersion(wallet: string, version: number): void {
  try {
    if (version > 0) localStorage.setItem(avatarVersionKey(wallet), String(version));
    else localStorage.removeItem(avatarVersionKey(wallet));
  } catch {
    // Storage unavailable (private mode / quota) — cosmetic only.
  }
}

interface ProfileContextValue {
  /** The logged-in user's editable profile, or null when logged out /
   *  not yet loaded. */
  profile: UserProfile | null;
  loading: boolean;
  /** Re-fetch from the server (after a mutation elsewhere). */
  refresh: () => Promise<void>;
  /** Optimistically replace the cached profile after a successful PATCH /
   *  avatar upload, so the header/profile update instantly. */
  setProfile: (p: UserProfile) => void;
  /** Uploaded-avatar URL for the current user, or null → generated. */
  avatarSrc: string | null;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * Loads (and caches) the logged-in user's backend profile — the source of
 * truth for the *custom* handle, bio and avatar. Wraps the app so the
 * header, profile dropdown, settings page and profile page all share one
 * fetch and stay in sync after edits.
 */
export function ProfileProvider({
  token,
  wallet,
  children,
}: {
  token: string | null;
  wallet: string | null;
  children: ReactNode;
}) {
  const [profile, setProfileState] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!token || !wallet) {
      setProfileState(null);
      return;
    }
    setLoading(true);
    try {
      const res = await usersApi.me(token);
      setProfileState(res.profile);
    } catch {
      // Non-fatal — header falls back to the Privy display name / generated
      // avatar. A failed /me shouldn't blank the UI.
    } finally {
      setLoading(false);
    }
  }, [token, wallet]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !wallet) {
      setProfileState(null);
      return;
    }
    setLoading(true);
    usersApi
      .me(token)
      .then((res) => !cancelled && setProfileState(res.profile))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, wallet]);

  // Until /me responds, fall back to the persisted last-known version so the
  // photo renders on the first paint instead of the generated initial.
  const avatarSrc = useMemo(
    () => avatarUrl(wallet, profile?.avatarVersion ?? readCachedAvatarVersion(wallet)),
    [wallet, profile?.avatarVersion],
  );

  useEffect(() => {
    // Only persist a profile that belongs to the current wallet (the state
    // can briefly hold the previous account's profile across a switch).
    if (!wallet || !profile) return;
    if (normalizeWallet(profile.wallet) !== normalizeWallet(wallet)) return;
    writeCachedAvatarVersion(wallet, profile.avatarVersion);
  }, [wallet, profile]);

  const value = useMemo<ProfileContextValue>(
    () => ({ profile, loading, refresh, setProfile: setProfileState, avatarSrc }),
    [profile, loading, refresh, avatarSrc],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

const EMPTY: ProfileContextValue = {
  profile: null,
  loading: false,
  refresh: async () => {},
  setProfile: () => {},
  avatarSrc: null,
};

/** Read the shared profile. Safe outside a provider (returns empty state). */
export function useProfile(): ProfileContextValue {
  return useContext(ProfileContext) ?? EMPTY;
}
